import { useState } from "react";
import { Link, createRoute } from "@tanstack/react-router";
import { staffRoute } from "./staff";
import { usePlotloungeRoomDetailQuery } from "@/lib/staff/queries";
import {
  useAssignFacilitatorMutation,
  useFacilitatorCandidatesQuery,
  useUnassignFacilitatorMutation,
} from "@/lib/staff/room-assignment";
import { StatusBadge } from "@/components/staff/StatusBadge";
import { SegmentProgress } from "@/components/staff/SegmentProgress";
import { shortDate } from "@/lib/staff/format";
import {
  SegmentSection,
  MembersSection,
  MargauxPostsSection,
  ReflectionsSection,
  SchedulerLogSection,
} from "@/components/room-detail/Sections";
import type { StaffFacilitatorAssignment } from "@/lib/staff/types";
import styles from "./staff-plotlounge-room-detail.module.scss";

function FacilitatorSection({
  roomId,
  status,
  assignments,
}: {
  roomId: string;
  status: string;
  assignments: StaffFacilitatorAssignment[];
}) {
  const active = assignments.find((a) => a.unassignedAt === null) ?? null;
  const history = assignments.filter((a) => a.unassignedAt !== null);
  const isRunning = status === "UPCOMING" || status === "ACTIVE";

  const candidatesQuery = useFacilitatorCandidatesQuery(isRunning && !active);
  const assignMut = useAssignFacilitatorMutation(roomId);
  const unassignMut = useUnassignFacilitatorMutation(roomId);

  const [pickedUserId, setPickedUserId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const candidates = candidatesQuery.data?.candidates ?? [];

  async function handleAssign() {
    if (!pickedUserId) return;
    setError(null);
    try {
      await assignMut.mutateAsync({ userId: pickedUserId });
      setPickedUserId("");
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to assign");
    }
  }

  async function handleUnassign() {
    if (!active) return;
    setError(null);
    if (!window.confirm("Remove this facilitator from the room?")) return;
    try {
      await unassignMut.mutateAsync({ userId: active.userId });
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to unassign");
    }
  }

  function userLabel(c: { user: StaffFacilitatorAssignment["user"] }) {
    return (
      c.user?.displayName || c.user?.username || c.user?.email || "(unnamed)"
    );
  }

  return (
    <section className={styles.card} aria-labelledby="facilitator-heading">
      <header className={styles.cardHead}>
        <h2 id="facilitator-heading" className={styles.cardTitle}>
          Facilitator
        </h2>
        <span className={styles.cardSubtitle}>
          {active ? "Assigned" : "None"}
        </span>
      </header>

      {active ? (
        <div className={styles.facCurrent}>
          <div>
            <span className={styles.facCurrentName}>
              {active.user?.displayName ||
                active.user?.username ||
                active.user?.email ||
                "(unnamed)"}
            </span>
            <span className={styles.facCurrentMeta}>
              assigned {shortDate(active.assignedAt)}
              {active.assignedBy
                ? ` by ${active.assignedBy.displayName || active.assignedBy.email}`
                : ""}
            </span>
          </div>
          <button
            type="button"
            className={styles.facUnassignBtn}
            onClick={handleUnassign}
            disabled={unassignMut.isPending}
          >
            {unassignMut.isPending ? "Unassigning…" : "Unassign"}
          </button>
        </div>
      ) : isRunning ? (
        <div className={styles.facPickerWrap}>
          <span className={styles.facPickerLabel}>Assign a facilitator</span>
          <div className={styles.facPickerRow}>
            <select
              className={styles.facPickerSelect}
              value={pickedUserId}
              onChange={(e) => setPickedUserId(e.target.value)}
              disabled={assignMut.isPending || candidatesQuery.isLoading}
              aria-label="Pick a facilitator"
            >
              <option value="">
                {candidatesQuery.isLoading
                  ? "Loading candidates…"
                  : candidates.length === 0
                    ? "No active facilitators available"
                    : "Choose a facilitator…"}
              </option>
              {candidates.map((c) => (
                <option key={c.userId} value={c.userId} disabled={c.atCap}>
                  {userLabel(c)}
                  {c.atCap ? " — at cap (2 active rooms)" : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={styles.facAssignBtn}
              onClick={handleAssign}
              disabled={!pickedUserId || assignMut.isPending}
            >
              {assignMut.isPending ? "Assigning…" : "Assign"}
            </button>
          </div>
          <span className={styles.facHint}>
            One facilitator per room; each can hold at most 2 active rooms.
          </span>
        </div>
      ) : (
        <div className={styles.empty}>
          Room is {status.toLowerCase()} — assignment is unavailable.
        </div>
      )}

      {error ? (
        <div className={styles.errorBanner} role="alert">
          {error}
        </div>
      ) : null}

      {history.length > 0 ? (
        <div>
          <header className={styles.cardHead}>
            <h3 className={styles.cardTitle}>History</h3>
            <span className={styles.cardSubtitle}>{history.length}</span>
          </header>
          {history.map((row) => (
            <div key={row.id} className={styles.facHistoryRow}>
              <span className={styles.facHistoryName}>
                {row.user?.displayName ||
                  row.user?.username ||
                  row.user?.email ||
                  "(unnamed)"}
              </span>
              <span className={styles.facHistoryMeta}>
                {shortDate(row.assignedAt)} → {shortDate(row.unassignedAt!)}
                {row.unassignedReason ? ` · ${row.unassignedReason}` : ""}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function PlotloungeRoomDetail() {
  const { id } = plotloungeRoomDetailRoute.useParams();
  const {
    data: room,
    isLoading,
    isError,
    error,
  } = usePlotloungeRoomDetailQuery(id);

  return (
    <main className={styles.shell}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/staff/plotlounge-rooms" className={styles.crumbLink}>
          Plotlounge rooms
        </Link>
        <span aria-hidden>/</span>
        <span>{room?.name ?? id}</span>
      </nav>

      {isError ? (
        <div className={styles.errorBanner} role="alert">
          Failed to load room: {(error as Error)?.message ?? "Unknown error"}
        </div>
      ) : null}

      {isLoading || !room ? (
        <div className={styles.empty}>Loading room…</div>
      ) : (
        <>
          <header className={styles.head}>
            <div>
              <h1 className={styles.title}>{room.name}</h1>
              <p className={styles.sub}>
                {room.book.title} — {room.book.author}
              </p>
              <div className={styles.statusRow}>
                <StatusBadge status={room.status} />
                <SegmentProgress
                  current={room.currentSegment}
                  total={room.totalSegments}
                  highlightCurrent={room.status === "ACTIVE"}
                />
                <span className={styles.sub}>
                  {shortDate(room.startDate)} → {shortDate(room.endDate)}
                </span>
              </div>
            </div>
          </header>

          <div className={styles.grid}>
            <div>
              <SegmentSection
                segments={room.segments}
                currentSegment={room.currentSegment}
                status={room.status}
              />
              <MargauxPostsSection posts={room.margauxPosts} />
              <ReflectionsSection reflections={room.reflections} />
            </div>
            <div>
              <FacilitatorSection
                roomId={room.id}
                status={room.status}
                assignments={room.facilitatorAssignments}
              />
              <MembersSection members={room.members} />
              <SchedulerLogSection entries={room.schedulerLog} />
            </div>
          </div>
        </>
      )}
    </main>
  );
}

export const plotloungeRoomDetailRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/plotlounge-rooms/$id",
  component: PlotloungeRoomDetail,
});
